import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import HowToSchema from '@/components/HowToSchema';
import { ArrowRight, Upload, CheckCircle } from 'lucide-react';

const STEPS = [
  { name: 'Upload your PDF or image', text: 'Upload an existing floor plan — a PDF, a scanned drawing, or a photo of a plan. It stays in your browser; nothing is sent to a server just to measure it.' },
  { name: 'Calibrate to a known dimension', text: 'Draw a line along one dimension you already know the real value of — a door width, a wall length, or a printed scale bar — and enter that value. Everything else on the page is now to true scale.' },
  { name: 'Measure lengths', text: 'Click along any wall or edge to measure its real-world length, straight or as a multi-point path.' },
  { name: 'Measure areas and rooms', text: 'Trace around a room or any shape to get its area. Add a label or room name directly on the plan as you go.' },
  { name: 'Verify against a known total', text: "If you know the property's total floor area or one room's area already, measure it first as a sanity check — if it matches, your calibration is correct and every other measurement on the page will be accurate too." },
];

const HowToMeasureFloorPlanOnline = () => {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Plan Measure", path: "/measurement-tool" },
    { name: "How to Measure a Floor Plan Online", path: "/guides/how-to-measure-a-floor-plan-online" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        pageId="guide-measure-online"
        defaultTitle="How to Measure a Floor Plan Online | House Plan Direct"
        defaultDescription="How to measure an existing floor plan online: upload a PDF or image, calibrate it to a known dimension, then measure lengths, areas and rooms for free in your browser."
        aiSummary="To measure a floor plan online: upload a PDF or image of the plan, draw a line along one dimension you already know and enter its real value to calibrate the page, then measure any length, area or room directly on top of the plan. Plan Measure, House Plan Direct's free browser-based tool, does this without uploading the file to a server."
        keywords="how to measure a floor plan, how to measure plans online, how to calibrate a floor plan, how to take measurements from a PDF plan, measure floor plan online free"
      />
      <HowToSchema
        steps={STEPS}
        name="How to Measure a Floor Plan Online"
        description="Step-by-step guide to measuring an existing floor plan online — upload, calibrate, then measure."
        url="https://houseplandirect.com/guides/how-to-measure-a-floor-plan-online"
      />
      <Header />
      <SeoBreadcrumbs items={breadcrumbs} />

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <Upload className="h-12 w-12 text-blue-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            How to Measure a Floor Plan Online
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Turn an existing PDF or image plan into accurate, real-world measurements — free, in your browser.
          </p>
          <Link to="/measurement-tool">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700" data-testid="guide-measure-link-tool">
              Open Plan Measure <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 mb-10">
            If you already have a plan — a PDF from an estate agent, a scanned architect's drawing, or a
            photo of one — you don't need to redraw it from scratch to get real measurements from it. Plan
            Measure lets you calibrate the existing drawing and measure directly on top of it.
          </p>

          <ol className="space-y-6 mb-12">
            {STEPS.map((step, i) => (
              <li key={step.name} className="flex gap-4" data-testid={`guide-measure-step-${i + 1}`}>
                <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">{i + 1}</div>
                <div>
                  <h2 className="font-semibold text-lg text-gray-900 mb-1">{step.name}</h2>
                  <p className="text-gray-600">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Why calibration matters</h2>
            <p className="text-gray-700">
              An uploaded PDF or image has no inherent real-world scale until you tell it one. Calibrating
              against a single known dimension fixes that for the whole page — so a length measured in one
              corner of the plan and an area measured in another are both correct, not just relative to
              each other.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><CheckCircle className="h-5 w-5 text-blue-600" /> Don't have a plan to upload?</h3>
              <p className="text-sm text-gray-600 mb-3">Draw one from scratch instead — enter exact wall lengths and House Plan Direct draws it to scale for you.</p>
              <Link to="/floor-plan-creator" className="text-blue-600 underline hover:text-blue-700 text-sm font-medium" data-testid="guide-measure-link-creator">Open the Floor Plan Creator →</Link>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><CheckCircle className="h-5 w-5 text-blue-600" /> Measuring for a property listing?</h3>
              <p className="text-sm text-gray-600 mb-3">Once measured, present the plan with room names, areas and GIA for a professional marketing floor plan.</p>
              <Link to="/estate-agent-floor-plans" className="text-blue-600 underline hover:text-blue-700 text-sm font-medium">See estate agent floor plans →</Link>
            </div>
          </div>

          <div className="bg-blue-600 text-white rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to measure your plan?</h2>
            <p className="text-blue-100 mb-6">Free to upload, calibrate and measure. Unlock the finished project for £2.99.</p>
            <Link to="/measurement-tool">
              <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100">
                Measure a Plan Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowToMeasureFloorPlanOnline;
